/**
 * Visual constants and alarm category configuration for the observability feature.
 *
 * Each category maps to a CloudWatch Composite Alarm with the same name as the
 * category label (created by ATMSMonitoringCDK ObservabilityStack).
 *
 * The UI calls the BFF once per category, passing the composite alarm name.
 * To add a new category: add a new entry here AND add the composite alarm in CDK.
 */
import { HealthStatus, STATUS_META } from '../types';

/**
 * Flat color lookup extracted from STATUS_META for use in inline styles.
 * Exists as a separate object so HistoryBar can use it as a simple
 * Record<HealthStatus, string> without reaching into STATUS_META's nested shape.
 */
export const STATUS_COLORS: Record<HealthStatus, string> = Object.fromEntries(
  Object.entries(STATUS_META).map(([key, meta]) => [key, meta.color])
) as Record<HealthStatus, string>;

export const TOOLTIP_STYLES = {
  backgroundColor: 'var(--awsui-color-background-container-content)',
  color: 'var(--awsui-color-text-body-default)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
} as const;

export const SLACK_BUTTON_STYLES = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: '#4A154B',
  color: '#fff',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
} as const;

/**
 * How often an open page re-fetches health data. Composite alarms re-evaluate every minute;
 * each refresh costs about 24 CloudWatch calls in the BFF, so a few minutes is the compromise.
 */
export const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Slack channel for alarm notifications — only shown to internal (Amazon) users.
 * Update this URL when the bot moves to a different channel.
 */
export const SLACK_CHANNEL_URL = 'https://amazon.enterprise.slack.com/archives/C0BJU5U0NQJ';

/**
 * Category configuration — single source of truth for categories in the UI.
 *
 * The composite alarm name equals the category label (set in CDK).
 * Each category maps to one BFF call: GET /api/observability/categoryAlarmHistory?alarmName=<label>
 */
export interface AlarmCategory {
  id: string;
  /** Display name AND the composite alarm name passed to the BFF as the alarmName query param. */
  label: string;
  description: string;
}

export const ALARM_CATEGORIES: AlarmCategory[] = [
  {
    id: 'core',
    label: 'Core Processing',
    description:
      'Monitors the ATMS web application and file converter — the core services that handle project creation, job management, and file processing.',
  },
  {
    id: 'translation-memory',
    label: 'Translation Memory',
    description:
      'Monitors the translation memory search cluster and bridge service — used for pre-translation and leveraging past translations.',
  },
  {
    id: 'termbase',
    label: 'Termbase',
    description:
      'Monitors the terminology database cluster and lookup latency — provides term consistency during translation.',
  },
  {
    id: 'translation-editor',
    label: 'Translation Editor',
    description:
      'Monitors the web editor (TWE) where translators work — if down, translators cannot translate.',
  },
  {
    id: 'offers',
    label: 'Offers',
    description:
      'Monitors the offer and assignment pipeline — how translation work gets routed and assigned to translators.',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    description:
      'Monitors email delivery and event processing — how users get notified about project and job updates.',
  },
  {
    id: 'scoring',
    label: 'Scoring',
    description:
      'Monitors the quality scoring service — measures translation quality after delivery.',
  },
];
